import { useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { PageHeaderSlotContext } from "./page-header-slot";

/**
 * Lets a page put its own control — in practice its "create" button — into the
 * shell's title bar, beside the notification bell.
 *
 * The bar belongs to `AppShell`, not to the page: it draws the one `<h1>` for
 * every screen and stays put while the list under it scrolls. So a page cannot
 * simply render its button there, and the alternative the pages used before —
 * a button in a row of its own directly under the bar — put a second, competing
 * header on every list screen.
 *
 * This is a PORTAL rather than a piece of context state holding a node. A node
 * in state has to be set from an effect, and the node is a fresh object on
 * every render, so the effect re-runs, sets state, and renders again: a loop
 * that only stops if the caller remembers to memoise its own JSX. Portalling
 * into a target element has no such trap — the element's identity is stable, so
 * the page just renders its children and React puts them in the bar.
 */

/**
 * The shell renders this inside the title bar's `actions`. `display: contents`
 * so it adds no box of its own to the bar's flex row — the portalled control
 * lays out as if it had been written there.
 */
export function PageHeaderSlotTarget({
  onMount,
}: {
  onMount: (el: HTMLElement | null) => void;
}) {
  return <span ref={onMount} className="contents" />;
}

/**
 * Render a page's title-bar control. Renders nothing until the shell's slot
 * exists, which also makes it a no-op on the login route, where there is no bar.
 */
export function PageHeaderActions({ children }: { children: ReactNode }) {
  const slot = useContext(PageHeaderSlotContext);
  if (!slot) return null;
  return createPortal(children, slot);
}
